import { useCallback, useEffect, useState } from "react";
import { UserData } from "modules/authentication/types/user-data";
import { useFetch } from "modules/main/hooks/useFetch";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { settingsLocationEndPoint, updateLocationEndPoint } from "modules/main/services/endpoints";
import { LocationResponse } from "modules/settings/types/location-types";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { LOCATION_UPDATED_EVENT } from "modules/main/services/location-storage";
import {
  LocationSelectorFields,
  type LocationSelectorFieldsChangePayload,
  type LocationSelectorFieldsValue,
} from "./location-selector-fields";

interface LocationSelectorProps {
    userData: UserData;
    onLocationSelected: () => void;
}

/** Schema retained for the submit path so the existing zod contract is unchanged. */
const locationFormSchema = z.object({
    city_id: z.string().min(1, { message: "Debes seleccionar una comuna" }),
    location: z.string().min(1, { message: "Debes seleccionar un sector" }),
});
type LocationFormValues = z.infer<typeof locationFormSchema>;

/**
 * Settings dialog location selector.
 *
 * Wraps the shared `LocationSelectorFields` and keeps the original
 * `updateLocationEndPoint` + `userData` write + `LOCATION_UPDATED_EVENT`
 * dispatch path intact. Onboarding consumes the same fields in
 * external-submit mode (see `OnboardingPage`) without touching this file.
 */
export const LocationSelector: React.FC<LocationSelectorProps> = ({
    userData,
    onLocationSelected,
}) => {
    const { isLogged } = useAuth();

    const requestOptions: FetchDataOptions = {
        method: "GET",
        headers: {},
        credentials: "include",
    };

    const { data: locationData, loading, error } = useFetch<LocationResponse>(
        settingsLocationEndPoint,
        requestOptions,
    );

    const initialValue: LocationSelectorFieldsValue = {
        cityId: userData.city_id?.toString() ?? "",
        locationId: userData.location_id?.toString() ?? "",
    };

    const [value, setValue] = useState<LocationSelectorFieldsValue>(initialValue);

    const handleSelectionChange = useCallback(
        (payload: LocationSelectorFieldsChangePayload) => {
            setValue({ cityId: payload.cityId, locationId: payload.locationId });
        },
        [],
    );

    /**
     * `LocationSelectorFields` is presentational and does not apply the
     * legacy `city.id !== 4` filter. The filter is preserved at the wrapper
     * level so the existing dialog behavior is unchanged.
     */
    const visibleCities = (locationData?.cities ?? []).filter(
        (city) => city.id !== 4,
    );

    const updateLocalStorage = (data: LocationFormValues) => {
        userData.city_id = parseInt(data.city_id);
        userData.location_id = parseInt(data.location);

        // Find the selected city and location to store their names
        const selectedCity = locationData?.cities.find(
            (city) => city.id === parseInt(data.city_id),
        );
        const selectedLocation = locationData?.locations.find(
            (loc) => loc.id === parseInt(data.location),
        );

        if (selectedCity) {
            userData.city_name = selectedCity.name;
        }

        if (selectedLocation) {
            userData.location_name = selectedLocation.name;
        }

        localStorage.setItem("userData", JSON.stringify(userData));
        window.dispatchEvent(new CustomEvent(LOCATION_UPDATED_EVENT));
    };

    const onSubmit = async (data: LocationFormValues) => {
        try {
            if (!isLogged) {
                updateLocalStorage(data);
                onLocationSelected();
                return;
            }

            const formData = {
                ...data,
                location: parseInt(data.location),
            };

            const response = await fetch(updateLocationEndPoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Error al actualizar los datos");
            }

            const responseData = (await response.json()) as {
                status: boolean;
                message?: string;
            };
            if (responseData.status) {
                toast.info("Se actualizó correctamente tu ubicación");
                updateLocalStorage(data);
                onLocationSelected();
            } else {
                toast.error(
                    responseData.message ||
                        "Ha ocurrido un error al actualizar tu ubicación",
                );
            }
        } catch (e) {
            toast.error("Ha ocurrido un error al actualizar tu ubicación");
        }
    };

    useEffect(() => {
        if (error) {
            toast.error("No se pudieron cargar las ubicaciones");
        }
    }, [error]);

    useEffect(() => {
        const handleLocationUpdated = () => {
            const localUserData = JSON.parse(
                localStorage.getItem("userData") ?? "{}",
            ) as Partial<UserData>;
            setValue({
                cityId: localUserData.city_id?.toString() ?? "",
                locationId: localUserData.location_id?.toString() ?? "",
            });
        };

        window.addEventListener(LOCATION_UPDATED_EVENT, handleLocationUpdated as EventListener);
        return () => {
            window.removeEventListener(LOCATION_UPDATED_EVENT, handleLocationUpdated as EventListener);
        };
    }, []);

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const parseResult = locationFormSchema.safeParse({
            city_id: value.cityId,
            location: value.locationId,
        });
        if (!parseResult.success) {
            const firstError = parseResult.error.issues[0]?.message;
            toast.error(firstError ?? "Debes seleccionar una comuna y un sector");
            return;
        }
        void onSubmit(parseResult.data);
    };

    return (
        <form onSubmit={handleFormSubmit} className="flex flex-col space-y-4">
            <LocationSelectorFields
                cities={visibleCities}
                locations={locationData?.locations ?? []}
                loading={loading}
                value={value}
                onChange={handleSelectionChange}
            />
            <p className="text-xs text-muted-foreground">
                Selecciona primero una comuna para ver los sectores disponibles
            </p>
            <Button type="submit">Actualizar</Button>
        </form>
    );
};
